import { ITableProps } from 'components/curo-components/common/Table/Table';
import { AvailableOffersTableVariables, CuroApplicationVariables } from 'enums/curo/Variables';
import { TableValue } from 'product_modules/api/Types';
import { ICuroApplication } from 'types/curo/CuroApplication';
import { formatToMonetary, formatToMonth, formatToPercent } from 'utils/curo-helpers/formatters';

export const FIND_BRANCH_BUTTON_TEXT = 'Find Branch';
export const CALL_US_BUTTON_TEXT = 'Call Us';
export const GO_TO_HOMEPAGE_BUTTON_TEXT = 'Go to Homepage';
export const APPLY_NOW = 'Apply Now';

export interface ISelectedLoanOfferItem {
  [offerId: string]: {
    loanOfferData: {
      accountNumber: string;
      firstPaymentDate: string;
      loanAmount: number;
      apr: number;
      termInMonths: number;
      monthlyPayment: number;
    };
  };
}

export interface ISelectedRefinanceLoanOfferItem {
  [offerId: string]: {
    loanOfferData: {
      accountNumber: string;
      newLoanAmount: number;
      payoffAmount: number;
      cashToCustomer: number;
      firstPaymentDate: string;
      apr: number;
      termInMonths: number;
      monthlyPayment: number;
    };
  };
}

type LoanOfferDataKeys = keyof ISelectedLoanOfferItem[string]['loanOfferData'] | keyof ISelectedRefinanceLoanOfferItem[string]['loanOfferData'];

export const loanOfferLabels: Record<LoanOfferDataKeys, string> = {
  accountNumber: 'Account Number',
  firstPaymentDate: 'First Payment Date',
  loanAmount: 'Loan Amount',
  apr: 'APR',
  termInMonths: 'Term',
  monthlyPayment: 'Monthly Payment',
  newLoanAmount: 'New Loan Amount',
  payoffAmount: 'Amount Paid On Existing Loan',
  cashToCustomer: 'Amount Paid To You',
};

export const loanOfferValue: Record<LoanOfferDataKeys, (value: number) => string> = {
  accountNumber: (value) => value.toString(),
  newLoanAmount: (value) =>
    formatToMonetary(value, {
      minimumFractionDigits: 2,
    }),
  loanAmount: (value) =>
    formatToMonetary(value, {
      minimumFractionDigits: 2,
    }),
  apr: (value) =>
    formatToPercent(value, {
      minimumFractionDigits: 2,
    }),
  firstPaymentDate: (value) => value.toString(),
  termInMonths: (value) => formatToMonth(value),
  monthlyPayment: (value) =>
    formatToMonetary(value, {
      minimumFractionDigits: 2,
    }),
  payoffAmount: (value) =>
    formatToMonetary(value, {
      minimumFractionDigits: 2,
    }),
  cashToCustomer: (value) =>
    formatToMonetary(value, {
      minimumFractionDigits: 2,
    }),
};

const formatRowValueToLoanOfferItem = (
  rowValue: TableValue[number], 
  accountNumber: string, 
  firstPaymentDate: string,
): ISelectedLoanOfferItem => {
  return {
    [rowValue._id]: {
      loanOfferData: {
        accountNumber,
        firstPaymentDate,
        loanAmount: rowValue[AvailableOffersTableVariables.LoanAmount] as number,
        apr: rowValue[AvailableOffersTableVariables.Apr] as number,
        termInMonths: rowValue[AvailableOffersTableVariables.TermInMonths] as number,
        monthlyPayment: rowValue[AvailableOffersTableVariables.MonthlyPayment] as number,
      },
    },
  };
};

const formatRefinanceRowValueToLoanOfferItem = (
  rowValue: TableValue[number], 
  accountNumber: string, 
  firstPaymentDate: string,
): ISelectedRefinanceLoanOfferItem => {
  return {
    [rowValue._id]: {
      loanOfferData: {
        accountNumber,
        newLoanAmount: rowValue[AvailableOffersTableVariables.LoanAmount] as number,
        payoffAmount: rowValue[CuroApplicationVariables.PayoffAmount] as number,
        cashToCustomer: rowValue[CuroApplicationVariables.CashToCustomer] as number,
        firstPaymentDate,
        apr: rowValue[AvailableOffersTableVariables.Apr] as number,
        termInMonths: rowValue[AvailableOffersTableVariables.TermInMonths] as number,
        monthlyPayment: rowValue[AvailableOffersTableVariables.MonthlyPayment] as number,
      },
    },
  };
};

export const parseLoanOffersToLoanOfferItem = (
  variables: ICuroApplication['variables'],
): ISelectedLoanOfferItem => {
  const selectedLoanOffer = variables[CuroApplicationVariables.SelectedLoanOffer] as TableValue;
  const bankAccountNumber = variables[CuroApplicationVariables.BankAccountNumber] as string;
  const firstPaymentDate = variables[CuroApplicationVariables.FirstPaymentDate] as string;

  return selectedLoanOffer.reduce((acc, cur) => {
    const formattedRowValue = formatRowValueToLoanOfferItem(cur, bankAccountNumber, firstPaymentDate);

    return { ...acc, ...formattedRowValue };
  }, {});
};

export const parseRefinanceLoanOffersToLoanOfferItem = (
  variables: ICuroApplication['variables'],
): ISelectedRefinanceLoanOfferItem => {
  const selectedLoanOffer = variables[CuroApplicationVariables.SelectedLoanOffer] as TableValue;
  const bankAccountNumber = variables[CuroApplicationVariables.BankAccountNumber] as string;
  const firstPaymentDate = variables[CuroApplicationVariables.FirstPaymentDate] as string;
  const payoffAmount = variables[CuroApplicationVariables.PayoffAmount] as number;
  const cashToCustomer = variables[CuroApplicationVariables.CashToCustomer] as number;

  return selectedLoanOffer.reduce((acc, cur) => {
    const formattedRowValue = formatRefinanceRowValueToLoanOfferItem({
      ...cur,
      [CuroApplicationVariables.PayoffAmount]: payoffAmount,
      [CuroApplicationVariables.CashToCustomer]: cashToCustomer,
    }, 
    bankAccountNumber, 
    firstPaymentDate,
  );

    return { ...acc, ...formattedRowValue};
  }, {});
};

const boldValues: LoanOfferDataKeys[] = ['firstPaymentDate', 'apr', 'monthlyPayment', 'newLoanAmount', 'cashToCustomer'];

export const formatLoanOfferDataToTableData = ({
 offerData, 
 origGpsLoanOrAccountNumber,
}: {
  offerData: ISelectedLoanOfferItem[string]['loanOfferData'] | ISelectedRefinanceLoanOfferItem[string]['loanOfferData'],
  origGpsLoanOrAccountNumber?: string,
}): ITableProps['data'] => {
  return (Object.entries(offerData) as Array<[LoanOfferDataKeys, number]>).map(([key, value]) => {
    const isBold = boldValues.includes(key);
    const valueFontWeight = isBold ? 'bold' : 'normal';
    const labelFontWeight = isBold ? 'bold' : 'normal';
    const label = key === 'payoffAmount' && origGpsLoanOrAccountNumber 
      ? `${loanOfferLabels[key]}: ${origGpsLoanOrAccountNumber}` 
      : loanOfferLabels[key];

    return {
      label: label,
      value: loanOfferValue[key](value),
      valueFontWeight,
      labelFontWeight,
    };
  });
}

export const getTableRows = (isRefinance: boolean): number => (isRefinance ? 8 : 6);
